// src/pages/DashboardPage.tsx
import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { LogOut, User, TestTube2 } from 'lucide-react';
import logoCorge from '../assets/logos/logo-corgee.png';
import { authStorage } from '../lib/storage.ts';

interface UserInfo {
  email: string;
  name?: string;
  provider?: string;
}

export const DashboardPage: React.FC = () =>
{
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() =>
  {
    loadUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  const loadUserInfo = async () =>
  {
    try
    {
      const access_token = authStorage.getAccessToken();

      // Solo validar que exista el token
      if (!access_token)
      {
        console.warn('⚠️ DashboardPage: No hay access_token, redirigiendo al login');
        setShouldRedirect(true);
        return;
      }

      // Obtener la información del usuario desde localStorage
      try
      {
        const username = authStorage.getUsername() || '';
        const givenName = authStorage.getGivenName() || username || '';

        setUser({
          email: username,
          name: givenName,
          provider: 'OAuth',
        });
      }
      catch (apiError)
      {
        // Si falla, usar los datos del token
        console.warn('No se pudo obtener info del localStorage:', apiError);
        const tokenData = parseJWT(access_token);
        setUser({
          email:
            tokenData.email ||
            tokenData['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ||
            'Usuario',
          name: tokenData.name || tokenData.unique_name || tokenData.sub || 'Usuario',
          provider:
            tokenData['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || 'OAuth',
        });
      }
    }
    catch (error)
    {
      console.error('Error loading user info:', error);
      handleLogout();
    }
    finally
    {
      setLoading(false);
    }
  };

  const parseJWT = (token: string) =>
  {
    try
    {
      const base64Url = token.split('.')[1] ?? '';
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    }
    catch (error)
    {
      return {};
    }
  };

  const handleLogout = () =>
  {
    authStorage.clearAuth();
    setShouldRedirect(true);
  };

  // Si necesitamos redirigir, hacerlo con Navigate
  if (shouldRedirect)
  {
    return <Navigate to="/" replace />;
  }

  if (loading)
  {
    return (
      <div className="min-h-screen bg-[#ededed] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5dade2] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#ededed]">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoCorge} alt="Corgee Logo" className="w-10 h-10 object-contain" />
            <h1 className="text-2xl font-semibold text-[#5dade2]">Corgee Dashboard</h1>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm"
            style={{ maxWidth: '7rem' }}
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-md p-8">
          <div className="flex items-center gap-4 mb-6">
            <div
              className="w-14 h-14 bg-[#5dade2] rounded-full flex items-center justify-center flex-shrink-0"
              style={{ maxWidth: '3.5rem' }}
            >
              <User className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-800">
                ¡Bienvenido, {user?.name || 'Usuario'}!
              </h2>
              <p className="text-gray-500 text-sm mt-1">Has iniciado sesión correctamente</p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Información de la cuenta</h3>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <span className="text-gray-600 font-medium">Email:</span>
                <span className="text-gray-800">{user?.email}</span>
              </div>

              {user?.name && (
                <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600 font-medium">Nombre:</span>
                  <span className="text-gray-800">{user.name}</span>
                </div>
              )}

              {user?.provider && (
                <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600 font-medium">Proveedor:</span>
                  <span className="text-gray-800 capitalize">{user.provider}</span>
                </div>
              )}

              <div className="flex items-center justify-between py-3 px-4 bg-green-50 rounded-lg border border-green-200">
                <span className="text-green-700 font-medium">Estado:</span>
                <span className="text-green-800 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Autenticado
                </span>
              </div>
            </div>
          </div>

          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>✓ Sesión activa:</strong> Tu sesión está protegida y activa.
            </p>
          </div>

          {/* Test Components Link */}
          <div className="mt-6 p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg">
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ maxWidth: '3rem' }}
              >
                <TestTube2 className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Probar Componentes Migrados
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Prueba todos los componentes de la Fase 2 que hemos migrado: Dialog, Buttons,
                  ImageViewer, CopyButton, Toast Notifications, Infinite Scroll y más.
                </p>
                <Link to="/components-test">
                  <button className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium">
                    <TestTube2 className="w-4 h-4" />
                    Ir a Página de Pruebas
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-gray-400">
          Corgee © CGBOT {new Date().getFullYear()}
        </div>
      </main>
    </div>
  );
};
